import { EndProductEmittingMethodInput } from '@/api/EmissionReportModel'
import { OrganizationDocumentUpload } from '@/shared/components'
import { UnitLabel } from '@/shared/config/config'
import {
  useReEvaluateActivityLevelValidationForMethodEmissionReport,
  useUpdateEmissionReport
} from '@/hooks/api/emissionReports'
import useI18n from '@/shared/hooks/useI18n'
import useOrganizationDocuments from '@/shared/hooks/useOrganizationDocuments'
import {
  Button,
  CheckboxGroup,
  GenericDialog,
  InputGroup,
  NumericInputGroup
} from '@/common'
import { randomIntExcluding } from '@/common/utils/randomIntExcluding'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

type State = {
  materialName: string
  quantityInTonnes: string
  proofFilePath: string
  confirmation: boolean
  internalId: string | null
}

const getInitialState = (initialState?: EndProductEmittingMethodInput): State => ({
  materialName: initialState?.materialName ?? '',
  quantityInTonnes: initialState?.quantityInTonnes?.toString() ?? '',
  proofFilePath: initialState?.proofFilePath ?? '',
  confirmation: initialState ? true : false,
  internalId: initialState?.internalId ?? null
})

export default function EndProductEmittingMethodInputDialog({
  emissionReportId,
  methodEmissionReportId,
  isOpen,
  close,
  initialState,
  endProductEmittingMethodInputs
}: {
  emissionReportId: string
  methodEmissionReportId: string
  isOpen: boolean
  close: () => void
  initialState?: EndProductEmittingMethodInput
  endProductEmittingMethodInputs: EndProductEmittingMethodInput[]
}) {
  const { t } = useI18n()
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUploadingProof, setIsUploadingProof] = useState(false)

  const { mutateAsync: updateEmissionReport } = useUpdateEmissionReport(emissionReportId)

  const {
    mutateAsync: reEvaluateActivityLevelValidationForMethodEmissionReport,
    isPending: isUpdatingActivityLevelValidation
  } = useReEvaluateActivityLevelValidationForMethodEmissionReport(
    emissionReportId,
    methodEmissionReportId
  )

  const { resetDocumentUploadState } = useOrganizationDocuments()

  const createIdForEndProductEmittingMethodInput = useCallback(() => {
    const existingEndProductEmittingMethodIds = endProductEmittingMethodInputs.map(
      (endProductEmittingMethodInput: EndProductEmittingMethodInput) =>
        endProductEmittingMethodInput.id
    )
    return randomIntExcluding(existingEndProductEmittingMethodIds)
  }, [endProductEmittingMethodInputs])

  const [state, setState] = useState<State>(getInitialState(initialState))

  useEffect(() => {
    setIsEditMode(!!initialState)
    if (initialState) {
      setState(getInitialState(initialState))
      return
    }
    setState(getInitialState())
  }, [initialState])

  const { materialName, quantityInTonnes, proofFilePath, confirmation, internalId } = state

  const resetState = useCallback(() => {
    setState(getInitialState(initialState))
    setIsEditMode(!!initialState)
    resetDocumentUploadState()
  }, [setState, resetDocumentUploadState, initialState])

  const onAddClick = useCallback(async () => {
    try {
      await updateEmissionReport({
        endProductEmittingMethodInputs: [
          ...endProductEmittingMethodInputs,
          {
            id: createIdForEndProductEmittingMethodInput(),
            type: 'OTHER_MATERIAL',
            materialName: materialName,
            proofFilePath: proofFilePath,
            quantityInTonnes: Number(quantityInTonnes),
            internalId: !internalId ? null : internalId
          }
        ]
      })
      toast.success(t('report:materials.endProductEmittingMethodInputs.dialog.toast.addedSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('report:materials.endProductEmittingMethodInputs.dialog.toast.addedError'))
    }

    reEvaluateActivityLevelValidationForMethodEmissionReport()

    resetState()
    close()
  }, [
    resetState,
    close,
    updateEmissionReport,
    endProductEmittingMethodInputs,
    createIdForEndProductEmittingMethodInput,
    materialName,
    proofFilePath,
    quantityInTonnes,
    internalId,
    reEvaluateActivityLevelValidationForMethodEmissionReport,
    t
  ])

  const onEditClick = useCallback(async () => {
    if (!initialState) {
      return
    }

    const updatedEndProductEmittingMethodInputs = endProductEmittingMethodInputs.map(
      endProductEmittingMethodInput =>
        endProductEmittingMethodInput.id === initialState.id
          ? {
              ...endProductEmittingMethodInput,
              materialName: materialName,
              quantityInTonnes: Number(quantityInTonnes),
              proofFilePath: proofFilePath,
              internalId: !internalId ? null : internalId
            }
          : endProductEmittingMethodInput
    )

    try {
      await updateEmissionReport({
        endProductEmittingMethodInputs: updatedEndProductEmittingMethodInputs
      })
      toast.success(t('report:materials.endProductEmittingMethodInputs.dialog.toast.updateSuccess'))
    } catch (error) {
      console.error(error)
      toast.error(t('report:materials.endProductEmittingMethodInputs.dialog.toast.updateError'))
    }

    reEvaluateActivityLevelValidationForMethodEmissionReport()

    resetState()
    close()
  }, [
    resetState,
    close,
    updateEmissionReport,
    endProductEmittingMethodInputs,
    initialState,
    quantityInTonnes,
    proofFilePath,
    materialName,
    internalId,
    reEvaluateActivityLevelValidationForMethodEmissionReport,
    t
  ])

  const onClose = useCallback(() => {
    resetState()
    close()
  }, [resetState, close])

  const isAddOrEditEnabled = useMemo(() => {
    return materialName && quantityInTonnes && confirmation
  }, [materialName, quantityInTonnes, confirmation])

  return (
    <GenericDialog
      isOpen={isOpen}
      size="xl"
      close={onClose}
      title={
        isEditMode
          ? t('report:materials.endProductEmittingMethodInputs.dialog.editTitle')
          : t('report:materials.endProductEmittingMethodInputs.dialog.addTitle')
      }
    >
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <InputGroup
              value={materialName}
              onChange={evt => setState({ ...state, materialName: evt.target.value })}
              label={t(
                'report:materials.endProductEmittingMethodInputs.dialog.fields.material.label'
              )}
            />
          </div>
          <div>
            <NumericInputGroup
              label={t(
                'report:materials.endProductEmittingMethodInputs.dialog.fields.quantityInTonnes.label'
              )}
              value={quantityInTonnes}
              suffix={UnitLabel.t}
              onChange={evt => setState({ ...state, quantityInTonnes: evt.target.value })}
              numericFormat={{
                decimalScale: 3,
                allowNegative: false
              }}
            />
          </div>
        </div>
        <InputGroup
          label={t(
            'report:materials.endProductEmittingMethodInputs.dialog.fields.internalId.label'
          )}
          value={internalId ?? ''}
          onChange={evt => setState({ ...state, internalId: evt.target.value })}
        />
        <OrganizationDocumentUpload
          label={t('report:materials.endProductEmittingMethodInputs.dialog.fields.evidence.label')}
          fileName={proofFilePath}
          onDocumentUploaded={file => setState(st => ({ ...st, proofFilePath: file }))}
          onDocumentDeleted={() => setState(st => ({ ...st, proofFilePath: '' }))}
          disabled={false}
          onUploading={setIsUploadingProof}
        />
        <CheckboxGroup
          className="mr-2"
          label={t(
            'report:materials.endProductEmittingMethodInputs.dialog.fields.confirmation.label'
          )}
          id="confirmation"
          checked={confirmation}
          onChange={checked => setState({ ...state, confirmation: checked })}
        />
      </div>
      <footer className="gap-2 flex justify-end">
        <Button
          variant="neutral"
          onClick={onClose}
          className="min-w-24"
          disabled={isUploadingProof}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button
          disabled={!isAddOrEditEnabled || isUploadingProof || isUpdatingActivityLevelValidation}
          onClick={isEditMode ? onEditClick : onAddClick}
          className="min-w-24"
        >
          {isEditMode ? t('common:actions.edit') : t('common:actions.add')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
